import { createApi, createCustomServiceCall } from '@thinknimble/tn-models'
import { axiosInstance } from '../axios-instance'
import {
  contactFilterShape,
  contactSearchResultShape,
  contactShape,
  conversationCreateShape,
  conversationFilterShape,
  conversationShape,
  messageCreateShape,
  messageFilterShape,
  mediaItemShape,
  messageShape,
  createWhatsappTemplateShape,
  templateCheckStatusResultShape,
  whatsappTemplateFilterShape,
  whatsappTemplateShape,
} from './models'
import { z } from 'zod'

// Custom call to mark conversation as seen
const markSeenCall = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: conversationShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/mark-seen/`)
    return fromApi(response.data)
  },
})

// Custom call to search contacts (guests with best-fit reservation)
const contactSearchCall = createCustomServiceCall({
  inputShape: z.string(),
  outputShape: z.array(z.object(contactSearchResultShape)),
  cb: async ({ client, slashEndingBaseUri, input }) => {
    const response = await client.get(`${slashEndingBaseUri}contact-search/`, {
      params: { q: input },
    })
    return response.data
  },
})

// Custom call to link reservation to conversation
const linkReservationCall = createCustomServiceCall({
  inputShape: {
    conversationId: z.string().uuid(),
    reservationId: z.string().uuid(),
  },
  outputShape: conversationShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.patch(
      `${slashEndingBaseUri}${input.conversationId}/link-reservation/`,
      { reservation_id: input.reservationId },
    )
    return fromApi(response.data)
  },
})

// Conversation API
export const conversationApi = createApi({
  client: axiosInstance,
  baseUri: '/whatsapp/conversations/',
  models: {
    entity: conversationShape,
    create: conversationCreateShape,
    extraFilters: conversationFilterShape,
  },
  customCalls: {
    markSeen: markSeenCall,
    linkReservation: linkReservationCall,
    contactSearch: contactSearchCall,
  },
})

// Custom call to upload an attachment and get back a Twilio-reachable URL.
// A browser object URL can't be fetched by Twilio, so the file is stored
// server-side first and the returned url is what `mediaUrls` on create expects.
const uploadMediaCall = createCustomServiceCall({
  inputShape: { file: z.custom<File>() },
  outputShape: mediaItemShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const data = new FormData()
    data.append('file', input.file)
    const response = await client.post(`${slashEndingBaseUri}upload-media/`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return fromApi(response.data)
  },
})

// Message API
export const messageApi = createApi({
  client: axiosInstance,
  baseUri: '/whatsapp/messages/',
  models: {
    entity: messageShape,
    create: messageCreateShape,
    extraFilters: messageFilterShape,
  },
  customCalls: {
    uploadMedia: uploadMediaCall,
  },
})

// Org contact directory — the new-conversation flow picks an existing contact
// from here instead of requiring a guest search result.
export const contactApi = createApi({
  client: axiosInstance,
  baseUri: '/whatsapp/contacts/',
  models: {
    entity: contactShape,
    extraFilters: contactFilterShape,
  },
})

// Custom call to force an immediate approval-status check for one pending
// template, rather than waiting on the periodic sync sweep.
const checkTemplateStatusCall = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: templateCheckStatusResultShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/check-status/`)
    return fromApi(response.data)
  },
})

// Reversibly hides a template (status → disabled) without deleting the row.
const disableTemplateCall = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: whatsappTemplateShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/disable/`)
    return fromApi(response.data)
  },
})

// Restores a disabled template to the status it had before disable (no resubmit).
const enableTemplateCall = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: whatsappTemplateShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/enable/`)
    return fromApi(response.data)
  },
})

// Submits a `draft` to Meta: the server validates body + bindings (400 with
// `body` / `variable_bindings` field errors) and flips it to `pending`.
const submitTemplateCall = createCustomServiceCall({
  inputShape: z.string().uuid(),
  outputShape: whatsappTemplateShape,
  cb: async ({ client, slashEndingBaseUri, input, utils: { fromApi } }) => {
    const response = await client.post(`${slashEndingBaseUri}${input}/submit/`)
    return fromApi(response.data)
  },
})

// Template API — the hub composer lists approved rows (read); the templates
// management page (org admins) creates templates and disables them.
//
// `create` authors the row AND submits it to Meta for approval in a single
// backend call — a created template comes back `pending` — unless `submit` is
// false, which saves a `draft` that `csc.submit` sends later. `remove` hard-deletes the row (freeing
// its name/language slot); `csc.disable` is the reversible alternative. Messages
// already sent from the template are unaffected either way.
export const whatsappTemplateApi = createApi({
  client: axiosInstance,
  baseUri: '/whatsapp/templates/',
  models: {
    entity: whatsappTemplateShape,
    create: createWhatsappTemplateShape,
    extraFilters: whatsappTemplateFilterShape,
  },
  customCalls: {
    checkStatus: checkTemplateStatusCall,
    disable: disableTemplateCall,
    enable: enableTemplateCall,
    submit: submitTemplateCall,
  },
})
